import { RecordType } from 'capacitor-health-connect';

import { PatientSubmissionDataTypes } from './enums';

export enum WearableDataSource {
  HealthConnect = 1,
  Apple,
  Withings,
  Task,
}

export enum HealthConnectDataPoints {
  Steps = 'Steps',
  BloodPressure = 'BloodPressure',
  Weight = 'Weight',
  Height = 'Height',
  Bpm = 'HeartRateSeries',
  Spo2 = 'OxygenSaturation',
  BloodGlucose = 'BloodGlucose',
}

export enum HealthKitDataPoints {
  Steps = 'steps',
  Distance = 'distance',
  Calories = 'calories',
  Weight = 'weight',
  Bpm = 'heartRate',
  Spo2 = 'oxygenSaturation',
  BloodPressureSystolic = 'bloodPressureSystolic',
  BloodPressureDiastolic = 'bloodPressureDiastolic',
  Sleep = 'activity',
  BloodGlucose = 'bloodGlucose',
}

export enum WearableDataDBType {
  Weight = 1,
  Height,
  Steps,
  Calories,
  Bpm,
  Sleep,
  Distance,
  BloodPressure,
  Ecg,
  Spo2,
  BloodGlucose,
}

type healthConnectPermissionsMapperType = {
  'android.permission.health.READ_STEPS': RecordType;
  'android.permission.health.READ_BLOOD_PRESSURE': RecordType;
  'android.permission.health.READ_WEIGHT': RecordType;
  'android.permission.health.READ_HEIGHT': RecordType;
  'android.permission.health.READ_HEART_RATE': RecordType;
  'android.permission.health.READ_OXYGEN_SATURATION': RecordType;
  'android.permission.health.READ_BLOOD_GLUCOSE': RecordType;
};

export const healthConnectPermissionsMapper: healthConnectPermissionsMapperType =
  {
    'android.permission.health.READ_STEPS': HealthConnectDataPoints.Steps,
    'android.permission.health.READ_BLOOD_PRESSURE':
      HealthConnectDataPoints.BloodPressure,
    'android.permission.health.READ_WEIGHT': HealthConnectDataPoints.Weight,
    'android.permission.health.READ_HEIGHT': HealthConnectDataPoints.Height,
    'android.permission.health.READ_HEART_RATE': HealthConnectDataPoints.Bpm,
    'android.permission.health.READ_OXYGEN_SATURATION':
      HealthConnectDataPoints.Spo2,
    'android.permission.health.READ_BLOOD_GLUCOSE':
      HealthConnectDataPoints.BloodGlucose,
  };

export type WearableBase<T> = {
  value: T;
  wearableDataTypeId: WearableDataDBType;
  wearableSourceTypeId: WearableDataSource;
};

export type WearableResponse<T = BloodPressure | number | string> =
  WearableBase<T> & {
    signal?: number[];
    heartRate?: number;
    classification?: string;
    unixEpochs: number;
    patientUUID: string;
  };

export type WearableData<T = BloodPressure | number | string> =
  WearableBase<T> & {
    key: string;
    isMedical: boolean;
  };

export type BloodPressure = {
  systolic: number;
  diastolic: number;
};

export type WearableDataObject = {
  wearableData: WearableResponse[];
  date: string;
  type: PatientSubmissionDataTypes.Wearable;
  lastSync: string;
  firebaseId: string;
  source: WearableDataSource;
  scopes: [];
};

export enum HealthConnectTimeRangeFilter {
  Between = 'between',
  Before = 'before',
  After = 'after',
}

export enum WithingsScopes {
  Metrics = 'user.metrics',
  Activity = 'user.activity',
}

export type WithingsTokens = {
  withingsUserId: string;
  withingsToken: string;
  withingsRefreshToken: string;
};

export type WearableScopes = Record<
  | 'steps'
  | 'weight'
  | 'height'
  | 'distance'
  | 'calories'
  | 'sleep'
  | 'bpm'
  | 'blood_pressure'
  | 'ecg'
  | 'spo2',
  boolean
>;

export enum WearableDataType {
  Steps = 'steps',
  Weight = 'weight',
  Height = 'height',
  Distance = 'distance',
  Calories = 'calories',
  Sleep = 'sleep',
  Bpm = 'bpm',
  BloodPressure = 'blood_pressure',
  Spo2 = 'spo2',
  Ecg = 'ecg',
}

export enum BloodGlucoseUnits {
  mmoll = 'millimolesPerLiter',
  mgdl = 'milligramsPerDeciliter',
}
